// import './utils/jsBridge' is for initializing the JSBridge utility, which is used for communication between the web app and native mobile app components.
import jsBridge from './utils/jsBridge';
import './tailwind.css';
import App from '@components/App';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import '@duxton-web/inter';
import '@duxton-web/grab-community-solid-static';
import Storage from 'grab-storage';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
import './globalStyles.less';
import { getEnv, initI18n, isDevEnv, isProdEnv } from './utils';
import trackerInstance from './utils/tracking/tracker';
import { ConfigProvider } from '@duxton-web/ui';
import { getLang } from '@utils/i18n';
import { LANG } from './constants/lang';

if (!isProdEnv() && jsBridge.canUseBridge()) {
  import('vconsole')
    .then((VConsole) => {
      // eslint-disable-next-line no-new, new-cap
      new VConsole.default();
    })
    .catch((err) => {
      console.error('Failed to load vConsole:', err);
    });
}

window.Grab = {
  config: {},
  storage: Storage,
};

async function renderApp() {
  await initI18n();
  const lang = getLang();
  const localeMap: Record<string, string> = {
    [LANG.CN]: 'zh_CN',
    [LANG.VN]: 'vi_VN',
    [LANG.ID]: 'id_ID',
    [LANG.TH]: 'th_TH',
    [LANG.MY]: 'ms_MY',
    [LANG.EN]: 'en_US',
  };
  const locale = await import(`@duxton-web/ui/locale/${localeMap[lang] || 'en_US'}.js`).then((module) => module.default);

  const container = document.getElementById('root');
  if (!container) return;

  trackerInstance.initialize('mex-crm', isProdEnv());

  const root = createRoot(container);
  root.render(
    <Provider store={store}>
      <BrowserRouter basename={'/crm'}>
        <ConfigProvider locale={locale}>
          <App />
        </ConfigProvider>
      </BrowserRouter>
    </Provider>,
  );
}

window
  .fetch(require('config.json'))
  .then(async (configResponse) => {
    if (configResponse.status !== 200) return;
    window.Grab.config = await configResponse.json();

    const datadogConfigExists =
      window.Grab.config.datadog && window.Grab.config.datadog.clientToken && window.Grab.config.datadog.applicationId;

    if (datadogConfigExists && !isDevEnv()) {
      // Initialize Datadog Logs
      datadogLogs.init({
        clientToken: window.Grab.config.datadog.clientToken,
        site: 'datadoghq.com',
        service: window.Grab.config.appId,
        env: getEnv(),
        version: process.env.APP_VERSION,
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
      });

      // Initialize Datadog RUM
      datadogRum.init({
        applicationId: window.Grab.config.datadog.applicationId,
        clientToken: window.Grab.config.datadog.clientToken,
        site: 'datadoghq.com',
        service: window.Grab.config.appId,
        env: getEnv(),
        version: process.env.APP_VERSION,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 20,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
      });

      // User profile and Datadog user setup is now handled in App component
      // Start RUM session
      datadogRum.startSessionReplayRecording();
    }

    renderApp();
  })
  .catch((err) => {
    datadogLogs.logger.error(err.message, { error: { stack: err.stack } });
    console.error(err);
  });
